/**
 * Mocks for eposnow.api.booking routes (MSW patterns like star + `/v1/booking/...` on any host).
 * Enable with `REACT_APP_MSW_ENABLED=true` and ensure `worker.start()` runs (see `src/index.tsx`).
 */
import { http, HttpResponse } from 'msw';
import { coerceBookingApiEntityId } from '../../lib/bookingApi';

const MOCK_BUSINESS_ID = 'eea1f22c-9ff2-4944-b9d1-53a5c34ce122';
const MOCK_SLUG = 'demo-salon';

/** Matches swagger `StaffResponse.workingHours` (also returned by GET `.../staff/{id}/hours`). */
const mockStaffListWorkingHours = [1, 2, 3, 4, 5].map((d) => ({
  dayOfWeek: d,
  startTime: '09:00',
  endTime: '17:00',
  isActive: true,
}));

const mockStaff = [
  {
    id: 'staff-1',
    businessId: MOCK_BUSINESS_ID,
    displayName: 'Marcus Chen',
    email: 'marcus@example.com',
    phone: null,
    avatarUrl: null,
    role: 'staff',
    bio: null,
    active: true,
    eposNowId: 88001,
    workingHours: mockStaffListWorkingHours,
  },
  {
    id: 'staff-2',
    businessId: MOCK_BUSINESS_ID,
    displayName: 'Aisha Johnson',
    email: 'aisha@example.com',
    phone: null,
    avatarUrl: null,
    role: 'staff',
    bio: null,
    active: true,
    eposNowId: 88002,
    workingHours: mockStaffListWorkingHours,
  },
  /** Matches swagger: id 0 with eposNowId for HQ-only / not-yet-persisted row. */
  {
    id: 0,
    businessId: MOCK_BUSINESS_ID,
    displayName: 'Epos linked (draft)',
    email: null,
    active: true,
    eposNowId: 91001,
    workingHours: mockStaffListWorkingHours,
  },
];

/** svc-1: 50% deposit (£42.50 on £85) — matches public booking “deposit” UI demos. */
const mockServicesCore = [
  {
    id: 'svc-1',
    businessId: MOCK_BUSINESS_ID,
    name: "Women's Cut & Style",
    description: 'Includes consultation, shampoo, precision cut, and blow-dry styling.',
    category: 'Hair',
    durationMins: 60,
    bufferBeforeMins: 0,
    bufferAfterMins: 0,
    priceCents: 8500,
    currency: 'GBP',
    depositEnabled: true,
    depositType: 'PERCENT',
    depositValue: 50,
    depositPolicyText: null,
    active: true,
    staffIds: ['staff-1'],
  },
  {
    id: 'svc-2',
    businessId: MOCK_BUSINESS_ID,
    eposNowProductId: 50002,
    name: 'Express Facial',
    description: 'Revitalizing 30-minute facial with cleanse, exfoliation, and hydrating mask.',
    category: 'Skin',
    durationMins: 30,
    bufferBeforeMins: 0,
    bufferAfterMins: 0,
    priceCents: 6500,
    currency: 'GBP',
    depositEnabled: false,
    depositType: 'fixed',
    depositValue: 0,
    depositPolicyText: null,
    active: true,
    staffIds: ['staff-2'],
  },
];

/** Extra rows so admin offerings pagination (>20 per page) can be exercised in MSW. */
const mockServicesExtra = Array.from({ length: 22 }, (_, i) => {
  const n = i + 3;
  return {
    id: `svc-demo-${n}`,
    businessId: MOCK_BUSINESS_ID,
    name: `Demo offering ${n}`,
    description: 'Generated mock row for pagination.',
    category: n % 4 === 0 ? 'Hair' : n % 4 === 1 ? 'Skin' : 'General',
    durationMins: 45,
    bufferBeforeMins: 0,
    bufferAfterMins: 0,
    priceCents: 4000 + n * 100,
    currency: 'GBP',
    depositEnabled: false,
    depositType: 'fixed',
    depositValue: 0,
    depositPolicyText: null,
    active: n % 7 !== 0,
    staffIds: [] as string[],
  };
});

const mockServices = [...mockServicesCore, ...mockServicesExtra];

const mockCustomer = {
  id: 'cust-1',
  businessId: MOCK_BUSINESS_ID,
  firstName: 'Jane',
  lastName: 'Doe',
  email: 'jane@example.com',
  phone: '+441234567890',
  notes: null,
  createdAt: new Date().toISOString(),
};

/** Second customer — used for past manual / “Express Facial” style demos */
const mockCustomer2 = {
  id: 'cust-2',
  businessId: MOCK_BUSINESS_ID,
  firstName: 'Charles',
  lastName: 'Prince',
  email: 'str8Cash@hotmail.com',
  phone: null,
  notes: null,
  createdAt: new Date().toISOString(),
};

const mockCustomerVip = {
  id: 'cust-3',
  businessId: MOCK_BUSINESS_ID,
  firstName: 'Sam',
  lastName: 'Taylor',
  email: 'sam.taylor@example.com',
  phone: null,
  notes: 'Allergic to lavender oil — confirm before service.',
  createdAt: new Date().toISOString(),
};

const now = Date.now();
const tomorrow = (h: number, m: number, plusDays = 1) => {
  const d = new Date(now);
  d.setDate(d.getDate() + plusDays);
  d.setHours(h, m, 0, 0);
  return d;
};
const daysAgoAt = (days: number, h: number, m: number, durationMin: number) => {
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  start.setHours(h, m, 0, 0);
  const end = new Date(start.getTime() + durationMin * 60 * 1000);
  return { start, end };
};

const ts = () => new Date().toISOString();

/** Local wall times on “today” (relative to page load) — matches dashboard “Today’s bookings” filter. */
const todayAt = (h: number, m: number, durationMin: number) => {
  const start = new Date(now);
  start.setHours(h, m, 0, 0);
  const end = new Date(start.getTime() + durationMin * 60 * 1000);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
};

/** Fixed local wall times on 27 Mar (current year) — shows on dashboard when “today” is 27 Mar; also for calendar demos. */
const march27Slot = (startH: number, startM: number, endH: number, endM: number) => {
  const y = new Date(now).getFullYear();
  const start = new Date(y, 2, 27, startH, startM, 0, 0);
  const end = new Date(y, 2, 27, endH, endM, 0, 0);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
};

/** Rich set for calendar, bookings list, and booking detail modal (past/future, statuses, manual, payments). */
const mockBookingsList = [
  /* 1 — Future · confirmed · public · paid (default list row) */
  {
    id: 'bk-mock-1',
    businessId: MOCK_BUSINESS_ID,
    serviceId: 'svc-1',
    staffId: 'staff-1',
    customerId: 'cust-1',
    startAt: tomorrow(10, 0).toISOString(),
    endAt: tomorrow(10, 45).toISOString(),
    status: 'confirmed',
    notes: '',
    manageToken: 'mock-manage-token',
    priceCents: 5500,
    currency: 'GBP',
    stripeSessionId: null,
    stripePaymentIntentId: null,
    depositAmountCents: 0,
    paymentStatus: 'paid',
    source: 'public',
    createdAt: ts(),
    updatedAt: ts(),
    service: mockServices[0],
    staffMember: mockStaff[0],
    customer: mockCustomer,
  },
  /* 2 — Past · confirmed · manual · deposit · notes (ref badge BK-E83A6634) */
  (() => {
    const { start, end } = daysAgoAt(3, 11, 0, 30);
    return {
      id: 'e83a6634-0000-4000-8000-000000000001',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-2',
      staffId: 'staff-2',
      customerId: 'cust-2',
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      status: 'confirmed',
      notes: "I don't want to be in the same room as David Beckham please.",
      manageToken: 'mock-manage-token-2',
      priceCents: 6500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 3250,
      paymentStatus: 'paid',
      source: 'admin',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[1],
      staffMember: mockStaff[1],
      customer: mockCustomer2,
    };
  })(),
  /* 3 — Past · completed */
  (() => {
    const { start, end } = daysAgoAt(7, 14, 0, 45);
    return {
      id: 'bk-completed-001',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-1',
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      status: 'completed',
      notes: 'Client loved the colour match.',
      manageToken: 'mock-m3',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomer,
    };
  })(),
  /* 4 — Past · no_show */
  (() => {
    const { start, end } = daysAgoAt(1, 9, 0, 30);
    return {
      id: 'bk-noshow-001',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-2',
      staffId: 'staff-2',
      customerId: 'cust-1',
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      status: 'no_show',
      notes: '',
      manageToken: 'mock-m4',
      priceCents: 6500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'unpaid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[1],
      staffMember: mockStaff[1],
      customer: mockCustomer,
    };
  })(),
  /* 5 — Past · cancelled */
  (() => {
    const { start, end } = daysAgoAt(5, 15, 30, 45);
    const cancelledAt = new Date(start.getTime() + 3600000);
    return {
      id: 'bk-cancelled-001',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-1',
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      status: 'cancelled',
      notes: '',
      manageToken: 'mock-m5',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'not_required',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      cancelledAt: cancelledAt.toISOString(),
      cancelledBy: 'Reception',
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomer,
    };
  })(),
  /* 6 — Future · pending_payment (warning status) */
  {
    id: 'bk-pending-pay-001',
    businessId: MOCK_BUSINESS_ID,
    serviceId: 'svc-2',
    staffId: 'staff-2',
    customerId: 'cust-1',
    startAt: tomorrow(16, 0, 2).toISOString(),
    endAt: tomorrow(16, 30, 2).toISOString(),
    status: 'pending_payment',
    notes: 'Awaiting card authorisation.',
    manageToken: 'mock-m6',
    priceCents: 6500,
    currency: 'GBP',
    stripeSessionId: null,
    stripePaymentIntentId: null,
    depositAmountCents: 6500,
    paymentStatus: 'unpaid',
    source: 'public',
    createdAt: ts(),
    updatedAt: ts(),
    service: mockServices[1],
    staffMember: mockStaff[1],
    customer: mockCustomer,
  },
  /* 7 — Future · confirmed · unpaid */
  {
    id: 'bk-unpaid-001',
    businessId: MOCK_BUSINESS_ID,
    serviceId: 'svc-1',
    staffId: 'staff-1',
    customerId: 'cust-1',
    startAt: tomorrow(18, 15).toISOString(),
    endAt: tomorrow(19, 0).toISOString(),
    status: 'confirmed',
    notes: '',
    manageToken: 'mock-m7',
    priceCents: 5500,
    currency: 'GBP',
    stripeSessionId: null,
    stripePaymentIntentId: null,
    depositAmountCents: 0,
    paymentStatus: 'unpaid',
    source: 'public',
    createdAt: ts(),
    updatedAt: ts(),
    service: mockServices[0],
    staffMember: mockStaff[0],
    customer: mockCustomer,
  },
  /* 8 — Future · unassigned staff (detail modal “Assign staff”) */
  {
    id: 'bk-unassigned-001',
    businessId: MOCK_BUSINESS_ID,
    serviceId: 'svc-2',
    staffId: null as unknown as string,
    customerId: 'cust-1',
    startAt: tomorrow(12, 0).toISOString(),
    endAt: tomorrow(12, 30).toISOString(),
    status: 'confirmed',
    notes: 'Any stylist is fine.',
    manageToken: 'mock-m8',
    priceCents: 6500,
    currency: 'GBP',
    stripeSessionId: null,
    stripePaymentIntentId: null,
    depositAmountCents: 0,
    paymentStatus: 'not_required',
    source: 'admin',
    createdAt: ts(),
    updatedAt: ts(),
    service: mockServices[1],
    staffMember: null as (typeof mockStaff)[0] | null,
    customer: mockCustomer,
  },
  /* 9 — Future · party size chip + customer profile notes */
  {
    id: 'bk-party-001',
    businessId: MOCK_BUSINESS_ID,
    serviceId: 'svc-1',
    staffId: 'staff-1',
    customerId: 'cust-3',
    startAt: tomorrow(11, 0).toISOString(),
    endAt: tomorrow(11, 45).toISOString(),
    status: 'confirmed',
    notes: 'Bringing two guests for colour consult.',
    manageToken: 'mock-m9',
    priceCents: 5500,
    currency: 'GBP',
    stripeSessionId: null,
    stripePaymentIntentId: null,
    depositAmountCents: 0,
    paymentStatus: 'paid',
    source: 'public',
    partySize: 4,
    createdAt: ts(),
    updatedAt: ts(),
    service: mockServices[0],
    staffMember: mockStaff[0],
    customer: mockCustomerVip,
  },
  /* Today — two bookings at the same start time (different staff) for timeline / overlap demos */
  (() => {
    const { startAt, endAt } = todayAt(14, 0, 60);
    return {
      id: 'bk-today-overlap-a',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-1',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Mock: same slot as bk-today-overlap-b (Marcus).',
      manageToken: 'mock-today-overlap-a',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomer,
    };
  })(),
  (() => {
    const { startAt, endAt } = todayAt(14, 0, 60);
    return {
      id: 'bk-today-overlap-b',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-2',
      staffId: 'staff-2',
      customerId: 'cust-2',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Mock: same slot as bk-today-overlap-a (Aisha).',
      manageToken: 'mock-today-overlap-b',
      priceCents: 6500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[1],
      staffMember: mockStaff[1],
      customer: mockCustomer2,
    };
  })(),
  (() => {
    const { startAt, endAt } = todayAt(9, 30, 45);
    return {
      id: 'bk-today-morning',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-3',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Extra appointment on today’s calendar.',
      manageToken: 'mock-today-morning',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomerVip,
    };
  })(),
  /* 27 Mar (current year) — calendar list + two at identical time (different staff) */
  (() => {
    const { startAt, endAt } = march27Slot(10, 0, 10, 45);
    return {
      id: 'bk-mock-1-mar27',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-1',
      startAt,
      endAt,
      status: 'confirmed',
      notes: '',
      manageToken: 'mock-manage-token-mar27-1',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomer,
    };
  })(),
  (() => {
    const { startAt, endAt } = march27Slot(11, 0, 11, 45);
    return {
      id: 'bk-party-001-mar27',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-3',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Bringing two guests for colour consult.',
      manageToken: 'mock-m9-mar27',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      partySize: 4,
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomerVip,
    };
  })(),
  /** Same wall time as bk-party-001-mar27 (11:00) — different staff for overlap on 27 Mar */
  (() => {
    const { startAt, endAt } = march27Slot(11, 0, 11, 45);
    return {
      id: 'bk-march27-11-overlap-staff2',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-2',
      staffId: 'staff-2',
      customerId: 'cust-2',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Mock: parallel booking same slot as party row (Aisha).',
      manageToken: 'mock-march27-11-overlap',
      priceCents: 6500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'paid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[1],
      staffMember: mockStaff[1],
      customer: mockCustomer2,
    };
  })(),
  (() => {
    const { startAt, endAt } = march27Slot(12, 0, 12, 30);
    return {
      id: 'bk-unassigned-001-mar27',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-2',
      staffId: null as unknown as string,
      customerId: 'cust-1',
      startAt,
      endAt,
      status: 'confirmed',
      notes: 'Any stylist is fine.',
      manageToken: 'mock-m8-mar27',
      priceCents: 6500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'not_required',
      source: 'admin',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[1],
      staffMember: null as (typeof mockStaff)[0] | null,
      customer: mockCustomer,
    };
  })(),
  (() => {
    const { startAt, endAt } = march27Slot(18, 15, 19, 0);
    return {
      id: 'bk-unpaid-001-mar27',
      businessId: MOCK_BUSINESS_ID,
      serviceId: 'svc-1',
      staffId: 'staff-1',
      customerId: 'cust-1',
      startAt,
      endAt,
      status: 'confirmed',
      notes: '',
      manageToken: 'mock-m7-mar27',
      priceCents: 5500,
      currency: 'GBP',
      stripeSessionId: null,
      stripePaymentIntentId: null,
      depositAmountCents: 0,
      paymentStatus: 'unpaid',
      source: 'public',
      createdAt: ts(),
      updatedAt: ts(),
      service: mockServices[0],
      staffMember: mockStaff[0],
      customer: mockCustomer,
    };
  })(),
];

const mockBookingDetail = mockBookingsList[0];

/** Matches public `/config` + admin business — drives booking page header, profile block, and toggles. */
const mockBookingPageConfig = {
  pageTitle: '',
  tagline: 'Hair · Skin · Nails · Brooklyn',
  themeMode: 'light' as const,
  primaryColor: '#7c3aed',
  radius: 'xl' as const,
  logoUrl: 'https://picsum.photos/seed/luxe-logo/128/128',
  coverImageUrl: 'https://picsum.photos/seed/luxe-cover/1200/480',
  galleryUrls: [
    'https://picsum.photos/seed/luxe-g1/400/300',
    'https://picsum.photos/seed/luxe-g2/400/300',
    'https://picsum.photos/seed/luxe-g3/400/300',
  ],
  showPrices: true,
  showDurations: true,
  showStaff: true,
  showAddress: true,
  showPhone: true,
  poweredByEposNow: true,
};

const publicConfigBody = {
  companyId: MOCK_BUSINESS_ID,
  slug: MOCK_SLUG,
  name: 'Luxe Beauty Studio',
  description:
    'A premier beauty destination offering expert hair styling, coloring, nail artistry, and skin treatments in the heart of Brooklyn. Our talented team is dedicated to helping you look and feel your best.',
  address: '245 Elm Street, Suite 3, Brooklyn, NY 11201',
  phone: '(718) 555-0142',
  email: 'hello@luxbeauty.example.com',
  timezone: 'Europe/London',
  currency: 'GBP',
  defaultDepositEnabled: true,
  defaultDepositType: 'PERCENT',
  defaultDepositValue: 50,
  slotIncrementMins: 15,
  minLeadTimeMins: 60,
  maxBookingHorizonDays: 90,
  cancellationPolicyText:
    'Free cancellation up to 24 hours before your appointment. Late cancellations or no-shows may be subject to a fee.',
  brandingJson: null,
  bookingPageConfig: mockBookingPageConfig,
  bookingPageDraftConfig: { ...mockBookingPageConfig, tagline: '[Draft] Hair · Skin · Nails · Brooklyn' },
};

const reportBody = {
  currency: 'GBP',
  totalRevenue: 125000,
  totalBookings: 42,
  cancellationRate: 0.08,
  bookingsByStatus: {
    confirmed: 12,
    completed: 20,
    cancelled: 5,
    no_show: 3,
    pending_payment: 2,
  },
  revenueByService: [
    { name: "Women's Cut & Style", count: 18, revenue: 45000 },
    { name: 'Express Facial', count: 14, revenue: 38000 },
  ],
  bookingsByStaff: [
    { name: 'Marcus Chen', count: 22, revenue: 62000 },
    { name: 'Aisha Johnson', count: 20, revenue: 63000 },
  ],
  bookingsByDay: [
    { date: new Date().toISOString().slice(0, 10), count: 3, revenue: 12000 },
  ],
  bookings: mockBookingsList.slice(0, 6) as unknown[],
};

/** Maps UI mock customer rows to OpenAPI `CustomerResponse` (single `name`). */
function toCustomerResponse(c: {
  id: string;
  businessId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  notes: unknown;
  createdAt: string | null;
}) {
  return {
    id: c.id,
    businessId: c.businessId,
    email: c.email,
    name: `${c.firstName} ${c.lastName}`.trim(),
    phone: c.phone,
    notes: c.notes,
    metadata: [] as number[],
    createdAt: c.createdAt,
    updatedAt: c.createdAt,
  };
}

/** Maps nested mock booking list rows to flat `BookingResponse` fields for `GET .../customers/{id}/bookings`. */
function bookingToFlatApiResponse(b: (typeof mockBookingsList)[number]) {
  const st = String(b.status || "confirmed").toLowerCase();
  const status =
    st === "cancelled"
      ? "CANCELLED"
      : st === "completed"
        ? "COMPLETED"
        : st === "pending_payment"
          ? "PENDING_PAYMENT"
          : "CONFIRMED";
  const sid = b.staffId as string | null;
  return {
    id: String(b.id),
    businessId: b.businessId,
    serviceId: typeof b.serviceId === "string" && /^-?\d+$/.test(b.serviceId) ? Number(b.serviceId) : b.serviceId,
    staffId: sid != null && sid !== "" ? Number(sid) : null,
    customerEmail: b.customer.email,
    customerName: `${b.customer.firstName} ${b.customer.lastName}`.trim(),
    customerPhone: b.customer.phone,
    startAt: b.startAt,
    endAt: b.endAt,
    status,
    manageToken: b.manageToken,
    priceCents: b.priceCents,
    currency: b.currency,
    source: b.source,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    serviceName: b.service?.name,
    staffName: b.staffMember?.displayName ?? undefined,
  };
}

const mockWaitlistEntries = [
  {
    id: "wl-1",
    businessId: MOCK_BUSINESS_ID,
    customerEmail: "waitlist@example.com",
    customerName: "Alex Waitlist",
    customerPhone: null as string | null,
    serviceId: 1,
    staffId: null as number | null,
    requestedDate: "2026-06-15",
    notes: "Flexible on afternoon slots",
    status: "active",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const mockBlockoutRows = [
  {
    id: "bo-1",
    businessId: MOCK_BUSINESS_ID,
    staffId: null as number | null,
    startsAt: new Date(now).toISOString(),
    endsAt: new Date(now + 86400000).toISOString(),
    reason: "Team training",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

/** PagedListResponse for admin services/staff (matches GET admin bookings shape). */
function adminPagedListJson<T>(request: Request, allItems: T[], defaultLimit = 50) {
  const url = new URL(request.url);
  const page = Math.max(1, Number(url.searchParams.get('page') || '1') || 1);
  const limit = Math.min(200, Math.max(1, Number(url.searchParams.get('limit') || String(defaultLimit)) || defaultLimit));
  const total = allItems.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const slice = allItems.slice(start, start + limit);
  return HttpResponse.json({
    data: slice,
    metadata: {
      page,
      limit,
      totalPages,
      totalRecords: total,
      currentPageSize: slice.length,
    },
    _links: {
      baseUrl: null,
      firstPage: null,
      lastPage: null,
      nextPage: page < totalPages ? `${url.pathname}?page=${page + 1}&limit=${limit}` : null,
      previousPage: page > 1 ? `${url.pathname}?page=${page - 1}&limit=${limit}` : null,
    },
  });
}

const bookingApiHandlers = [
  /* ——— Admin ——— */
  http.get('*/v1/booking/admin/bookings', ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') || '1') || 1);
    const limit = Math.min(200, Math.max(1, Number(url.searchParams.get('limit') || '50') || 50));
    const total = mockBookingsList.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;
    const slice = mockBookingsList.slice(start, start + limit);
    return HttpResponse.json({
      data: slice,
      metadata: {
        page,
        limit,
        totalPages,
        totalRecords: total,
        currentPageSize: slice.length,
      },
      _links: {
        baseUrl: null,
        firstPage: null,
        lastPage: null,
        nextPage: page < totalPages ? `${url.pathname}?page=${page + 1}&limit=${limit}` : null,
        previousPage: page > 1 ? `${url.pathname}?page=${page - 1}&limit=${limit}` : null,
      },
    });
  }),
  http.get('*/v1/booking/admin/staff', ({ request }) => adminPagedListJson(request, mockStaff)),
  http.get('*/v1/booking/admin/services', ({ request }) => {
    const url = new URL(request.url);
    const activeOnly = url.searchParams.get('activeOnly') === 'true';
    const list = activeOnly
      ? mockServices.filter((s) => (s as { active?: boolean }).active !== false)
      : mockServices;
    return adminPagedListJson(request, list);
  }),
  http.get('*/v1/booking/admin/customers', ({ request }) => {
    const url = new URL(request.url);
    const q = url.searchParams.get("q")?.trim().toLowerCase();
    const all = [mockCustomer, mockCustomer2, mockCustomerVip].map((c) => toCustomerResponse(c));
    const filtered = q
      ? all.filter((c) => c.email.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
      : all;
    return adminPagedListJson(request, filtered);
  }),
  /** OpenAPI: GET returns `CustomerResponse` only (bookings: separate route). */
  http.get('*/v1/booking/admin/customers/:customerId', ({ params }) => {
    const id = String(params.customerId);
    const row = [mockCustomer, mockCustomer2, mockCustomerVip].find((c) => c.id === id);
    if (!row) {
      return HttpResponse.json({ success: false, message: "Not found" }, { status: 404 });
    }
    return HttpResponse.json(toCustomerResponse(row));
  }),
  http.get('*/v1/booking/admin/customers/:customerId/bookings', ({ params }) => {
    const id = String(params.customerId);
    const rows = mockBookingsList.filter((b) => b.customerId === id).map(bookingToFlatApiResponse);
    return HttpResponse.json(rows);
  }),
  http.patch('*/v1/booking/admin/customers/:customerId', async ({ request, params }) => {
    const id = String(params.customerId);
    const body = (await request.json()) as Record<string, unknown>;
    const base = [mockCustomer, mockCustomer2, mockCustomerVip].find((c) => c.id === id) ?? mockCustomer;
    const name =
      typeof body.name === "string" ? body.name.trim() : `${base.firstName} ${base.lastName}`.trim();
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? base.firstName;
    const lastName = parts.length > 1 ? parts.slice(1).join(" ") : base.lastName;
    const merged = {
      ...base,
      firstName,
      lastName,
      phone: typeof body.phone === "string" ? body.phone : base.phone,
      notes: typeof body.notes === "string" ? body.notes : base.notes,
    };
    return HttpResponse.json(toCustomerResponse(merged));
  }),
  http.get('*/v1/booking/admin/resources', ({ request }) =>
    adminPagedListJson(request, [
      {
        id: "res-1",
        businessId: MOCK_BUSINESS_ID,
        name: "Room 1",
        description: null,
        type: "room",
        capacity: 4,
        active: true,
        serviceIds: ["svc-1", "svc-2"],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ]),
  ),
  http.post('*/v1/booking/admin/resources', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    const rawSids = body.serviceIds;
    const serviceIds = Array.isArray(rawSids) ? rawSids : [];
    return HttpResponse.json(
      {
        id: "res-new",
        businessId: MOCK_BUSINESS_ID,
        name: String(body.name ?? "Resource"),
        description: body.description != null ? String(body.description) : null,
        type: body.type != null ? String(body.type) : null,
        capacity: typeof body.capacity === "number" ? body.capacity : 1,
        active: body.active !== false,
        serviceIds,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { status: 201 },
    );
  }),
  http.patch('*/v1/booking/admin/resources/:id', async ({ request }) => HttpResponse.json(await request.json())),
  http.delete('*/v1/booking/admin/resources/:id', () => new HttpResponse(null, { status: 204 })),
  /** Swagger: GET /v1/booking/admin/staff/{id}/hours */
  http.get('*/v1/booking/admin/staff/:staffId/hours', () =>
    HttpResponse.json(mockStaffListWorkingHours),
  ),
  http.get('*/v1/booking/admin/business', () => HttpResponse.json(publicConfigBody)),
  /** OpenAPI: POST register booking business (201 Created). */
  http.post('*/v1/booking/admin/business', async ({ request }) => {
    const raw = await request.json().catch(() => ({}));
    const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const merged = {
      ...publicConfigBody,
      ...(typeof body.name === "string" ? { name: body.name } : {}),
      ...(typeof body.slug === "string" ? { slug: body.slug } : {}),
      ...(typeof body.timezone === "string" ? { timezone: body.timezone } : {}),
      ...(Array.isArray(body.openingHours) ? { openingHours: body.openingHours } : {}),
    };
    return HttpResponse.json(merged, { status: 201 });
  }),
  http.patch('*/v1/booking/admin/business', async ({ request }) => HttpResponse.json(await request.json())),
  http.patch('*/v1/booking/admin/business/branding/draft', async ({ request }) => {
    const raw = await request.json().catch(() => ({}));
    const body = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return HttpResponse.json({
      ...publicConfigBody,
      ...("bookingPageDraftConfig" in body ? { bookingPageDraftConfig: body.bookingPageDraftConfig } : {}),
      ...("brandingDraftJson" in body ? { brandingDraftJson: body.brandingDraftJson } : {}),
      brandingUpdatedAt: new Date().toISOString(),
    });
  }),
  http.post('*/v1/booking/admin/business/branding/publish', async () =>
    HttpResponse.json({ ...publicConfigBody, brandingUpdatedAt: new Date().toISOString() }),
  ),
  /** Swagger: PATCH /v1/booking/admin/staff/{id}/hours — replaces working hours; responds with hour rows. */
  http.patch('*/v1/booking/admin/staff/:staffId/hours', async ({ request }) => {
    const raw = await request.json().catch(() => ({}));
    const body = (raw && typeof raw === "object" ? raw : {}) as { workingHours?: unknown[] };
    const rows = Array.isArray(body.workingHours) ? body.workingHours : [];
    return HttpResponse.json(rows);
  }),
  http.get('*/v1/booking/admin/waitlist', ({ request }) => adminPagedListJson(request, mockWaitlistEntries)),
  http.post('*/v1/booking/admin/waitlist/:id/notify', ({ params }) => {
    const id = String(params.id);
    const hit = mockWaitlistEntries.find((w) => w.id === id);
    return HttpResponse.json(hit ?? mockWaitlistEntries[0]);
  }),
  http.delete('*/v1/booking/admin/waitlist/:id', () => new HttpResponse(null, { status: 204 })),
  http.get('*/v1/booking/admin/blockouts', ({ request }) => {
    const url = new URL(request.url);
    if (!url.searchParams.get("start") || !url.searchParams.get("end")) {
      return HttpResponse.json({ success: false, message: "start and end required" }, { status: 400 });
    }
    return adminPagedListJson(request, mockBlockoutRows);
  }),
  http.post('*/v1/booking/admin/blockouts', async ({ request }) => {
    const raw = await request.json();
    const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    return HttpResponse.json(
      {
        id: "block-new",
        businessId: MOCK_BUSINESS_ID,
        startsAt: body.startsAt,
        endsAt: body.endsAt,
        reason: body.reason ?? null,
        staffId: body.staffId ?? null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { status: 201 },
    );
  }),
  http.delete('*/v1/booking/admin/blockouts/:id', () => new HttpResponse(null, { status: 204 })),
  http.get('*/v1/booking/admin/reports', () => HttpResponse.json(reportBody)),
  http.post('*/v1/booking/admin/bookings', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    const base = bookingToFlatApiResponse(mockBookingsList[0]);
    return HttpResponse.json({ ...base, ...body, id: "bk-new" }, { status: 201 });
  }),
  http.post('*/v1/booking/admin/bookings/:id/cancel', () =>
    HttpResponse.json({
      ...bookingToFlatApiResponse(mockBookingsList[0]),
      status: "CANCELLED",
      cancelledAt: new Date().toISOString(),
    }),
  ),
  http.patch('*/v1/booking/admin/bookings/:id', async ({ request }) => {
    const raw = await request.json();
    const body = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    return HttpResponse.json({ ...bookingToFlatApiResponse(mockBookingsList[0]), ...body });
  }),
  http.post('*/v1/booking/admin/bookings/:id/send-reminder', () =>
    HttpResponse.json(bookingToFlatApiResponse(mockBookingsList[0])),
  ),
  http.post('*/v1/booking/admin/bookings/:id/complete', () =>
    HttpResponse.json({ ...bookingToFlatApiResponse(mockBookingsList[0]), status: "COMPLETED" }),
  ),
  http.patch('*/v1/booking/admin/bookings/:id/assign-staff', () =>
    HttpResponse.json(bookingToFlatApiResponse(mockBookingsList[0])),
  ),
  http.patch('*/v1/booking/admin/bookings/:id/assign-resource', async ({ request }) => {
    const raw = await request.json().catch(() => ({}));
    const body = raw && typeof raw === "object" ? (raw as { resourceId?: string }) : {};
    return HttpResponse.json({
      ...bookingToFlatApiResponse(mockBookingsList[0]),
      resourceId: body.resourceId ?? null,
      resourceName: body.resourceId ? "Resource" : undefined,
    });
  }),
  http.post('*/v1/booking/admin/staff', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    return HttpResponse.json({ id: 'staff-new', ...body }, { status: 201 });
  }),
  http.patch('*/v1/booking/admin/staff/:id', async ({ request }) => HttpResponse.json(await request.json())),
  http.delete('*/v1/booking/admin/staff/:id', () => new HttpResponse(null, { status: 204 })),
  http.post('*/v1/booking/admin/services', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    return HttpResponse.json({ id: 'svc-new', ...body }, { status: 201 });
  }),
  http.patch('*/v1/booking/admin/services/:id', async ({ request }) => HttpResponse.json(await request.json())),
  http.delete('*/v1/booking/admin/services/:id', ({ params }) =>
    HttpResponse.json({
      id: Number(params.id) || 0,
      businessId: MOCK_BUSINESS_ID,
      name: "Deactivated service",
      durationMins: 30,
      bufferBeforeMins: 0,
      bufferAfterMins: 0,
      priceCents: 0,
      currency: "GBP",
      active: false,
      eposNowProductId: 0,
    }),
  ),
  http.post('*/v1/booking/admin/upload', () => HttpResponse.json({ url: 'https://example.com/mock-upload.png' })),

  /* ——— Public ——— */
  http.get('*/v1/booking/public/:slug/config', () => HttpResponse.json(publicConfigBody)),
  http.get('*/v1/booking/public/:slug/services', () => HttpResponse.json({ data: mockServices })),
  http.get('*/v1/booking/public/:slug/staff', () => HttpResponse.json({ data: mockStaff })),
  http.get('*/v1/booking/public/:slug/availability', ({ request }) => {
    const url = new URL(request.url);
    const dateStr = url.searchParams.get('date');
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return HttpResponse.json([]);
    }
    const [yy, mm, dd] = dateStr.split('-').map(Number);
    const slotHours = [10, 12, 14];
    const durationMs = 45 * 60 * 1000;
    const body = slotHours.map((hour) => {
      const start = new Date(yy, mm - 1, dd, hour, 0, 0, 0);
      return {
        startAt: start.toISOString(),
        endAt: new Date(start.getTime() + durationMs).toISOString(),
        staffId: 1,
      };
    });
    return HttpResponse.json({ data: body });
  }),
  http.post('*/v1/booking/public/:slug/bookings', async ({ request }) => {
    const raw = await request.json();
    const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    return HttpResponse.json({ ...body, id: "bk-created", checkoutUrl: null }, { status: 201 });
  }),
  /** OpenAPI: POST `/v1/booking/public/{slug}/waitlist` — `JoinWaitlistBody` → `WaitlistEntryResponse`. */
  http.post('*/v1/booking/public/:slug/waitlist', async ({ request }) => {
    const raw = await request.json();
    const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    return HttpResponse.json(
      {
        id: "wl-new",
        businessId: MOCK_BUSINESS_ID,
        customerEmail: String(body.customerEmail ?? ""),
        customerName: String(body.customerName ?? ""),
        customerPhone: body.customerPhone != null ? String(body.customerPhone) : null,
        serviceId: (() => {
          const c = coerceBookingApiEntityId(String(body.serviceId ?? ""));
          return typeof c === "number" && Number.isFinite(c) ? c : 1;
        })(),
        staffId: body.staffId != null ? Number(body.staffId) : null,
        requestedDate: String(body.requestedDate ?? ""),
        notes: body.notes != null ? String(body.notes) : null,
        status: "pending",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      { status: 201 },
    );
  }),
  http.get('*/v1/booking/public/booking/verify-payment', () =>
    HttpResponse.json({
      ...mockBookingDetail,
      ref: 'BK-MOCK',
    }),
  ),

  /* ——— Account / manage ——— */
  http.post('*/v1/booking/account/request-link', () => HttpResponse.json({ ok: true })),
  http.get('*/v1/booking/account/bookings', () =>
    HttpResponse.json({
      customer: mockCustomer,
      bookings: mockBookingsList.filter((b) => b.customerId === mockCustomer.id),
    }),
  ),
  http.get('*/v1/booking/manage/:token', () => HttpResponse.json(mockBookingDetail)),
  http.post('*/v1/booking/manage/:token', async ({ request }) => {
    const raw = await request.json().catch(() => ({}));
    const body = raw && typeof raw === "object" ? (raw as { action?: string }) : {};
    if (body.action === "cancel") {
      return HttpResponse.json({ ...mockBookingDetail, status: "CANCELLED" });
    }
    return HttpResponse.json(mockBookingDetail);
  }),
];

export default bookingApiHandlers;
